/**
 * WHAT: CRUD interface for targeted UAR scopes built from data sensitivity criteria.
 * WHY: Targeted scopes let admins focus user access reviews on high-risk data --
 *      e.g., "all users accessing CRITICAL-classified tables" -- instead of
 *      reviewing every user across every database.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';

/**
 * WHAT: Full lifecycle component for creating, evaluating, exporting, and deleting scopes.
 * WHY: Scopes translate classification criteria into user lists that feed UAR campaigns,
 *      bridging the PRO data governance module with the attestation workflow.
 */
const ProScopes = () => {
    const [scopes, setScopes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showUsersModal, setShowUsersModal] = useState(false);
    const [selectedScope, setSelectedScope] = useState(null);
    const [scopeUsers, setScopeUsers] = useState([]);
    const [evaluating, setEvaluating] = useState(null);
    const [classificationTypes, setClassificationTypes] = useState([]);

    const [formData, setFormData] = useState({
        scope_code: '',
        scope_name: '',
        description: '',
        scope_type: 'DYNAMIC',
        criteria: []
    });

    useEffect(() => {
        fetchScopes();
        fetchClassificationTypes();
    }, []);

    /* WHAT: Loads all targeted scopes from the backend.
     * WHY: Populates the scopes table with name, criteria count, user count, etc. */
    const fetchScopes = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/pro/scopes/`);
            setScopes(response.data.scopes || []);
        } catch (err) {
            console.error('Error fetching scopes:', err);
        } finally {
            setLoading(false);
        }
    };

    /* WHAT: Fetches classification type options for the scope criteria form.
     * WHY: When a scope criterion targets CLASSIFICATION, the admin picks from
     *      the available classification codes via a multi-select dropdown. */
    const fetchClassificationTypes = async () => {
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/pro/classification-types/`);
            setClassificationTypes(response.data.classification_types || []);
        } catch (err) {
            console.error('Error fetching classification types:', err);
        }
    };

    /* WHAT: Initialises the scope form with a default SENSITIVITY_LEVEL criterion and opens the modal.
     * WHY: Most scopes start with a sensitivity filter; providing a sensible default
     *      reduces the number of clicks for the most common use case. */
    const handleCreate = () => {
        setFormData({
            scope_code: '',
            scope_name: '',
            description: '',
            scope_type: 'DYNAMIC',
            criteria: [{ criteria_type: 'SENSITIVITY_LEVEL', operator: 'IN', criteria_values: ['CRITICAL'], criteria_group: 1 }]
        });
        setShowModal(true);
    };

    /* WHAT: Submits the new scope definition (code, name, criteria) to the backend.
     * WHY: Persists the scope so it can later be evaluated to produce a user list
     *      for targeted access reviews. */
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_CONFIG.BASE_URL}/api/pro/scopes/`, formData);
            setShowModal(false);
            fetchScopes();
        } catch (err) {
            alert('Failed to create scope: ' + (err.response?.data?.error || err.message));
        }
    };

    /* WHAT: Triggers server-side evaluation of a scope's criteria against current data.
     * WHY: Evaluation resolves dynamic criteria into a concrete list of users in scope,
     *      updating the user_count and asset_count displayed in the table. */
    const handleEvaluate = async (scopeId) => {
        setEvaluating(scopeId);
        try {
            const response = await axios.post(`${API_CONFIG.BASE_URL}/api/pro/scopes/${scopeId}/evaluate/`);
            alert(`Scope evaluated: ${response.data.users_in_scope} users found`);
            fetchScopes();
        } catch (err) {
            alert('Evaluation failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setEvaluating(null);
        }
    };

    /* WHAT: Fetches and displays the list of users matched by a scope's criteria.
     * WHY: Admins need to review which users will be included in a targeted UAR
     *      campaign before exporting the scope. */
    const handleViewUsers = async (scope) => {
        setSelectedScope(scope);
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/pro/scopes/${scope.scope_id}/users/`);
            setScopeUsers(response.data.users || []);
            setShowUsersModal(true);
        } catch (err) {
            alert('Failed to load users');
        }
    };

    /* WHAT: Deletes a scope after browser confirmation.
     * WHY: Scope deletion is irreversible and removes all associated criteria
     *      and evaluation results; confirmation prevents accidental data loss. */
    const handleDelete = async (scopeId) => {
        if (!window.confirm('Are you sure you want to delete this scope?')) return;
        try {
            await axios.delete(`${API_CONFIG.BASE_URL}/api/pro/scopes/${scopeId}/`);
            fetchScopes();
        } catch (err) {
            alert('Failed to delete scope');
        }
    };

    /* WHAT: Exports a scope's evaluated user list for use in a UAR campaign.
     * WHY: The export bridges PRO scopes to UAR campaigns -- the output summarises
     *      how many users and sources would be included, so admins can confirm before
     *      creating the campaign. */
    const handleExport = async (scopeId) => {
        try {
            const response = await axios.get(`${API_CONFIG.BASE_URL}/api/pro/scopes/${scopeId}/export/`);
            console.log('Export data:', response.data);
            alert(`Ready to create campaign for ${response.data.total_users} users from ${response.data.sources?.length || 0} sources`);
        } catch (err) {
            alert('Export failed');
        }
    };

    /* WHAT: Appends a new blank criterion row to the scope form.
     * WHY: Scopes can combine multiple criteria (e.g., CLASSIFICATION + SENSITIVITY_LEVEL);
     *      this lets admins build compound scope definitions in the create modal. */
    const addCriteria = () => {
        setFormData({
            ...formData,
            criteria: [...formData.criteria, {
                criteria_type: 'CLASSIFICATION',
                operator: 'IN',
                criteria_values: [],
                criteria_group: 1
            }]
        });
    };

    /* WHAT: Removes a criterion row from the scope form by index.
     * WHY: Admins may add a criterion by mistake or need to simplify the scope;
     *      the button is disabled when only one criterion remains. */
    const removeCriteria = (index) => {
        const newCriteria = formData.criteria.filter((_, i) => i !== index);
        setFormData({ ...formData, criteria: newCriteria });
    };

    /* WHAT: Updates a single field on a criterion row in the scope form.
     * WHY: Each criterion has independent type, operator, and values fields;
     *      immutable-style update ensures React detects the state change. */
    const updateCriteria = (index, field, value) => {
        const newCriteria = [...formData.criteria];
        newCriteria[index] = { ...newCriteria[index], [field]: value };
        setFormData({ ...formData, criteria: newCriteria });
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-1">Targeted Scopes</h4>
                    <p className="text-muted mb-0">
                        Define scopes for targeted User Access Reviews
                    </p>
                </div>
                <button className="btn btn-primary" onClick={handleCreate}>
                    <i className="fas fa-plus me-2"></i>
                    New Scope
                </button>
            </div>

            <div className="card">
                <div className="card-body p-0">
                    <table className="table table-hover mb-0">
                        <thead className="table-dark">
                            <tr>
                                <th>Scope</th>
                                <th>Type</th>
                                <th className="text-center">Criteria</th>
                                <th className="text-center">Users</th>
                                <th className="text-center">Assets</th>
                                <th>Last Evaluated</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {scopes.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-4 text-muted">
                                        No scopes defined. Click "New Scope" to create one.
                                    </td>
                                </tr>
                            ) : (
                                scopes.map(scope => (
                                    <tr key={scope.scope_id}>
                                        <td>
                                            <strong>{scope.scope_name}</strong>
                                            <div className="small text-muted">
                                                <code>{scope.scope_code}</code>
                                            </div>
                                            {scope.description && (
                                                <div className="small text-muted">{scope.description}</div>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`badge ${scope.scope_type === 'DYNAMIC' ? 'bg-primary' : 'bg-secondary'}`}>
                                                {scope.scope_type}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <span className="badge bg-info">{scope.criteria_count || 0}</span>
                                        </td>
                                        <td className="text-center">
                                            {scope.user_count > 0 ? (
                                                <button
                                                    className="btn btn-sm btn-link p-0"
                                                    onClick={() => handleViewUsers(scope)}
                                                >
                                                    <span className="badge bg-success">{scope.user_count}</span>
                                                </button>
                                            ) : (
                                                <span className="badge bg-secondary">0</span>
                                            )}
                                        </td>
                                        <td className="text-center">
                                            <span className="badge bg-info">{scope.asset_count || 0}</span>
                                        </td>
                                        <td>
                                            {scope.last_evaluated_at ? (
                                                <small>{new Date(scope.last_evaluated_at).toLocaleString()}</small>
                                            ) : (
                                                <span className="text-muted">Never</span>
                                            )}
                                        </td>
                                        <td className="text-end">
                                            <div className="btn-group btn-group-sm">
                                                <button
                                                    className="btn btn-outline-success"
                                                    onClick={() => handleEvaluate(scope.scope_id)}
                                                    disabled={evaluating === scope.scope_id}
                                                    title="Evaluate Scope"
                                                >
                                                    {evaluating === scope.scope_id ? (
                                                        <span className="spinner-border spinner-border-sm"></span>
                                                    ) : (
                                                        <i className="fas fa-play"></i>
                                                    )}
                                                </button>
                                                <button
                                                    className="btn btn-outline-primary"
                                                    onClick={() => handleExport(scope.scope_id)}
                                                    title="Export for Campaign"
                                                >
                                                    <i className="fas fa-file-export"></i>
                                                </button>
                                                <button
                                                    className="btn btn-outline-danger"
                                                    onClick={() => handleDelete(scope.scope_id)}
                                                    title="Delete"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create Scope Modal */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    <i className="fas fa-crosshairs me-2"></i>
                                    New Targeted Scope
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row g-3 mb-4">
                                        <div className="col-md-6">
                                            <label className="form-label">Scope Code *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.scope_code}
                                                onChange={(e) => setFormData({ ...formData, scope_code: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
                                                required
                                                placeholder="CRITICAL_DATA_ACCESS"
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label">Scope Name *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={formData.scope_name}
                                                onChange={(e) => setFormData({ ...formData, scope_name: e.target.value })}
                                                required
                                                placeholder="Critical Data Access"
                                            />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label">Description</label>
                                            <textarea
                                                className="form-control"
                                                rows="2"
                                                value={formData.description}
                                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <h6>Scope Criteria</h6>
                                    <p className="small text-muted">Define criteria to identify users in scope</p>

                                    {formData.criteria.map((criteria, idx) => (
                                        <div key={idx} className="card mb-3">
                                            <div className="card-body">
                                                <div className="row g-2 align-items-end">
                                                    <div className="col-md-4">
                                                        <label className="form-label small">Criteria Type</label>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={criteria.criteria_type}
                                                            onChange={(e) => updateCriteria(idx, 'criteria_type', e.target.value)}
                                                        >
                                                            <option value="CLASSIFICATION">Classification</option>
                                                            <option value="SENSITIVITY_LEVEL">Sensitivity Level</option>
                                                            <option value="USER_TYPE">User Type</option>
                                                            <option value="ACCESS_TYPE">Access Type</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-md-2">
                                                        <label className="form-label small">Operator</label>
                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={criteria.operator}
                                                            onChange={(e) => updateCriteria(idx, 'operator', e.target.value)}
                                                        >
                                                            <option value="IN">In</option>
                                                            <option value="NOT_IN">Not In</option>
                                                        </select>
                                                    </div>
                                                    <div className="col-md-5">
                                                        <label className="form-label small">Values</label>
                                                        {criteria.criteria_type === 'SENSITIVITY_LEVEL' ? (
                                                            <select
                                                                className="form-select form-select-sm"
                                                                multiple
                                                                value={criteria.criteria_values}
                                                                onChange={(e) => updateCriteria(idx, 'criteria_values',
                                                                    Array.from(e.target.selectedOptions, opt => opt.value))}
                                                            >
                                                                <option value="CRITICAL">CRITICAL</option>
                                                                <option value="HIGH">HIGH</option>
                                                                <option value="MEDIUM">MEDIUM</option>
                                                                <option value="LOW">LOW</option>
                                                            </select>
                                                        ) : criteria.criteria_type === 'CLASSIFICATION' ? (
                                                            <select
                                                                className="form-select form-select-sm"
                                                                multiple
                                                                value={criteria.criteria_values}
                                                                onChange={(e) => updateCriteria(idx, 'criteria_values',
                                                                    Array.from(e.target.selectedOptions, opt => opt.value))}
                                                            >
                                                                {classificationTypes.map(ct => (
                                                                    <option key={ct.classification_code} value={ct.classification_code}>
                                                                        {ct.classification_code}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                value={criteria.criteria_values.join(', ')}
                                                                onChange={(e) => updateCriteria(idx, 'criteria_values',
                                                                    e.target.value.split(',').map(v => v.trim()))}
                                                                placeholder="Value1, Value2"
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="col-md-1">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => removeCriteria(idx)}
                                                            disabled={formData.criteria.length <= 1}
                                                        >
                                                            <i className="fas fa-times"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addCriteria}>
                                        <i className="fas fa-plus me-2"></i>
                                        Add Criteria
                                    </button>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        <i className="fas fa-plus me-2"></i>
                                        Create Scope
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Users Modal */}
            {showUsersModal && selectedScope && (
                <div className="modal fade show" style={{
                    display: 'block',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: 1050,
                    overflow: 'auto'
                }}>
                    <div style={{ padding: '20px' }}>
                        <div className="modal-content" style={{ maxWidth: '1200px', width: '100%', margin: '0 auto' }}>
                            <div className="modal-header bg-primary text-white">
                                <h5 className="modal-title">
                                    <i className="fas fa-users me-2"></i>
                                    Users in Scope: {selectedScope.scope_name}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowUsersModal(false)}></button>
                            </div>
                            <div className="modal-body p-0">
                                <table className="table table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Username</th>
                                            <th>Source</th>
                                            <th>Type</th>
                                            <th>Max Sensitivity</th>
                                            <th>Evaluated</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {scopeUsers.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" className="text-center py-4 text-muted">
                                                    No users in scope
                                                </td>
                                            </tr>
                                        ) : (
                                            scopeUsers.map((user, idx) => (
                                                <tr key={idx}>
                                                    <td><strong>{user.username}</strong></td>
                                                    <td>
                                                        {user.source_name}
                                                        <span className="badge bg-secondary ms-2">{user.source_type}</span>
                                                    </td>
                                                    <td>{user.user_type || 'USER'}</td>
                                                    <td>
                                                        <span className={`badge ${
                                                            user.sensitivity_exposure?.max_sensitivity === 'CRITICAL' ? 'bg-danger' :
                                                            user.sensitivity_exposure?.max_sensitivity === 'HIGH' ? 'bg-warning text-dark' :
                                                            'bg-info'
                                                        }`}>
                                                            {user.sensitivity_exposure?.max_sensitivity || '-'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <small>{new Date(user.evaluated_at).toLocaleString()}</small>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowUsersModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProScopes;
